import { applyAceRecord, applyComplete, getComplete, stream } from "@cs124/monace";
import { PingWS, filterPingPongMessages } from "@cs124/pingpongws-client";
import base64url from "base64url";
import crypto from "crypto";
import { EventEmitter } from "events";
import queryString from "query-string";
import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import ReconnectingWebSocket from "reconnecting-websocket";
import { debounce } from "throttle-debounce";
import { ConnectionQuery, GetMessage, ServerMessages, UpdateMessage } from "./types";
export const MaceContext = createContext({
  available: false,
  connected: false,
  register: () => {
    throw new Error("Mace provider not set");
  }
});
const generateRandomID = () => base64url.fromBase64(crypto.randomBytes(20).toString("base64"));
export const MaceProvider = ({
  server,
  googleToken,
  shouldConnect = true,
  loggedIn,
  children
}) => {
  const [connected, setConnected] = useState(false);
  const _connected = useRef(false);
  const client = useRef(
    typeof window !== "undefined" && localStorage.getItem("mace:id") || generateRandomID()
  );
  const connection = useRef(void 0);
  const messager = useRef(new EventEmitter());
  const editors = useRef({});
  useEffect(() => {
    localStorage.setItem("mace:id", client.current);
    return () => {
      messager.current?.removeAllListeners();
    };
  }, []);
  const onOpen = useRef(() => {
    const values = [...new Set(Object.values(editors.current))];
    !_connected.current && values.forEach(
      (id) => connection.current?.readyState === ReconnectingWebSocket.OPEN && connection.current?.send(JSON.stringify(GetMessage.check({ type: "get", id, local: true })))
    );
    _connected.current = true;
    setConnected(true);
  });
  const onClose = useRef(() => {
    _connected.current = false;
    setConnected(false);
  });
  const onMessage = useRef(
    filterPingPongMessages(({ data }) => {
      const response = JSON.parse(data);
      if (!ServerMessages.guard(response)) {
        console.error(`Bad message: ${JSON.stringify(response, null, 2)}`);
        return;
      }
      if (UpdateMessage.guard(response) || GetMessage.guard(response)) {
        messager.current.emit(response.id, response);
      }
    })
  );
  const connectionType = useRef(void 0);
  useEffect(() => {
    if ((googleToken || loggedIn) && connectionType.current === "authorized" || !(googleToken || loggedIn) && connectionType.current === "unauthorized") {
      return;
    }
    connection.current?.close();
    connectionType.current = void 0;
    if (!shouldConnect) {
      return;
    }
    const connectionQuery = ConnectionQuery.check({
      client: client.current,
      googleToken
    });
    connection.current = PingWS(
      new ReconnectingWebSocket(`${server}?${queryString.stringify(connectionQuery)}`, [], { startClosed: true }),
      { useOtherMessages: true, interval: 32 * 1024, timeout: 16 * 1024 }
    );
    connection.current.addEventListener("open", onOpen.current);
    connection.current.addEventListener("close", onClose.current);
    connection.current.addEventListener("message", onMessage.current);
    connection.current.reconnect();
    connectionType.current = googleToken || loggedIn ? "authorized" : "unauthorized";
  }, [server, googleToken, shouldConnect, loggedIn]);
  const register = useCallback((request) => {
    const { id, editor, options } = request;
    const view = generateRandomID();
    editors.current[view] = id;
    const useServer = options?.useServer !== void 0 ? options.useServer : true;
    const autoSave = options?.autoSave !== void 0 ? options.autoSave : true;
    const autoSaveDelay = options?.autoSaveDelay !== void 0 ? options.autoSaveDelay : 1024;
    const streaming = options?.streaming !== void 0 ? options.streaming : false;
    let records = [];
    let isEnabled = true;
    let saving;
    let last;
    let lastSave;
    const save = (force = false) => {
      if (!isEnabled) {
        return;
      }
      if (records.length === 0 && !force) {
        return;
      }
      const save2 = generateRandomID();
      if (useServer) {
        saving = save2;
      }
      const toSave = streaming ? [...records] : [...records, getComplete(editor)];
      records = [];
      lastSave = (/* @__PURE__ */ new Date()).valueOf();
      const updateMessage = UpdateMessage.check({
        type: "update",
        id,
        view,
        save: save2,
        local: true,
        streaming,
        focused: editor.isFocused(),
        unixtime: lastSave,
        records: toSave
      });
      const updateMessageString = JSON.stringify(updateMessage);
      messager.current.emit(id, updateMessage);
      localStorage.setItem(`mace:${id}`, updateMessageString);
      useServer && connection.current?.send(updateMessageString);
      useServer && options?.onSaveStarted && options.onSaveStarted();
    };
    const autoSaver = debounce(autoSaveDelay, save);
    const messageListener = (record) => {
      if (!isEnabled) {
        return;
      }
      records.push(record);
      if (streaming) {
        save();
      } else {
        autoSave && autoSaver();
      }
    };
    const { stop: stopStream, pause, restart } = stream(editor, messageListener);
    const eventListener = (message) => {
      if (GetMessage.guard(message)) {
        !message.local && message.id === id && options?.onGetCompleted && options.onGetCompleted();
        return;
      }
      const update = UpdateMessage.check(message);
      if (useServer && update.view === view && update.save === saving && !update.local) {
        useServer && options?.onSaveCompleted && options.onSaveCompleted();
        saving = void 0;
        return;
      }
      if (!isEnabled || update.view === view || update.records.length === 0 || update.save === last) {
        return;
      }
      if (lastSave && update.unixtime < lastSave) {
        console.warn("Rejecting stale save message");
        return;
      }
      last = update.save;
      if (!streaming) {
        const record = update.records[update.records.length - 1];
        if (record.type !== "complete") {
          return;
        }
        pause();
        record && applyComplete(editor, record, editor.isFocused() && !record.focused);
        restart();
      } else {
        pause();
        for (const record of update.records) {
          applyAceRecord(editor, record);
        }
        restart();
      }
    };
    messager.current.addListener(id, eventListener);
    const enable = (enabled) => {
      !enabled && save();
      isEnabled = enabled;
    };
    const stop = () => {
      stopStream();
      messager.current.removeListener(id, eventListener);
      delete editors.current[id];
    };
    useServer && connection.current?.send(JSON.stringify(GetMessage.check({ type: "get", id, local: true })));
    try {
      const previous = localStorage.getItem(`mace:${id}`);
      if (previous && UpdateMessage.guard(JSON.parse(previous))) {
        eventListener(JSON.parse(previous));
      } else {
        localStorage.removeItem(`mace:${id}`);
      }
    } catch (err) {
    }
    return { save, enable, stop };
  }, []);
  return /* @__PURE__ */ React.createElement(MaceContext.Provider, { value: { available: true, connected, register } }, children);
};
export const useMace = () => {
  return useContext(MaceContext);
};
export { UpdateMessage as MaceUpdateMessage } from "./types";
//# sourceMappingURL=index.js.map
